import { client } from "./supabase";
import { exigirFechaValida } from "./validarFechaDocumento";

// Writer único de `plazocobro` del lado VENTA (Fase 5 "Futuros: reagendar"). Nace en B.3 Commit 12
// (2026-09-27, camino #13) reemplazando los 4 UPDATE/RPC crudos que vivían inline en
// `VincularEmitidaModal.js` (10.1) — `sales`/`sales_btl`/`sales_contratos.meses[]`/cartelería-hardware,
// misma escritura, mismo candado de fecha, un solo lugar. Reusado en B.3 Commit 13 (camino #16b) por
// `VentasFacturas/index.js` ("Guardar plazocobro inline") — mismas ramas, mismo writer.
//
// Cero cambio de comportamiento respecto al bloque que reemplaza: cada rama hace exactamente el
// mismo UPDATE/RPC que antes. Lo que cambia es que el error YA NO SE TRAGA (antes: `catch { console.
// error(...) }`, sin avisar en pantalla, 10.177) — ahora se propaga (`throw`) y el caller (el modal)
// lo muestra en un toast visible y frena la vinculación (no llega al toast de éxito ni cierra el modal).
//
// `esCarteleria`: predicado inyectado por el caller — evita reimplementar una TERCERA copia de
// `t === "negocios_carteleria" || t === "negocios_carteleria_hw"` (ya vive en `VincularEmitidaModal.js`
// y en `src/utils/lineasFacturacion/refKey.js`, ninguna de las dos exportada hoy; 10.1/10.243).
//
// B.3 Commit 13 (2026-09-27, camino #16b): sumó la rama HW-OPEX/Gestión — la línea de facturación
// puede no existir todavía (a diferencia de HW-CAPEX, que siempre tiene `linea_id`), así que ese caso
// se resuelve por CLAVE NATURAL vía `upsert_linea_facturacion` (crea o edita, 10.1) en vez de por
// `linea_id`. El caller decide cuál rama de cartelería aplica pasando `lineaId` (edita, HW-CAPEX) O
// `claveNatural` (upsert, HW-OPEX/Gestión) — nunca ambos.
//
// @param {Object} params
// @param {"sales"|"sales_btl"|"sales_contratos"|string} params.tipo - resultado.tipo del buscador.
// @param {number} params.id - id de la fila (sales/sales_btl/sales_contratos).
// @param {number|null} [params.refIndex] - resultado.ref_index — solo sales_contratos (posición del mes).
// @param {number|null} [params.lineaId] - lineaId ya resuelto — cartelería/hardware con línea existente.
// @param {{negocioId, tipo: "opex"|"gestion", ano, mes, tramo?}|null} [params.claveNatural] - cartelería
//   SIN línea resuelta todavía (HW-OPEX/Gestión) — upsert por clave natural.
// @param {string} params.nuevaFecha - "YYYY-MM-DD".
// @param {string} params.modo - uno de MODOS_VALIDACION. REQUERIDO (10.177, exigirFechaValida).
// @param {number|null} [params.usuarioId] - para la RPC de cartelería (auditoría).
// @param {(tipo: string) => boolean} params.esCarteleria - predicado de clasificación del caller.
export const guardarPlazocobroVenta = async ({
  tipo,
  id,
  refIndex = null,
  lineaId = null,
  claveNatural = null,
  nuevaFecha,
  modo,
  usuarioId = null,
  esCarteleria,
}) => {
  exigirFechaValida({ plazocobro: nuevaFecha }, "plazocobro", { modo });
  const usuarioIdInt = usuarioId != null ? parseInt(usuarioId, 10) || null : null;

  if (tipo === "sales") {
    const { error } = await client.from("sales").update({ plazocobro: nuevaFecha }).eq("id", id);
    if (error) throw error;
    return;
  }
  if (tipo === "sales_btl") {
    const { error } = await client.from("sales_btl").update({ plazocobro: nuevaFecha }).eq("id", id);
    if (error) throw error;
    return;
  }
  if (tipo === "sales_contratos" && refIndex != null) {
    // Contratos: siguen posicionales (fuera de alcance de líneas de facturación) — mismo patrón que
    // ya usaba el bloque original: leer `meses`, pisar la posición, reescribir el array completo.
    const { data, error: errSel } = await client.from("sales_contratos").select("meses").eq("id", id).single();
    if (errSel) throw errSel;
    if (data?.meses) {
      const meses = [...data.meses];
      meses[refIndex] = { ...meses[refIndex], plazocobro: nuevaFecha };
      const { error: errUpd } = await client.from("sales_contratos").update({ meses }).eq("id", id);
      if (errUpd) throw errUpd;
    }
    return;
  }
  if (esCarteleria(tipo) && lineaId != null) {
    // L6 tramo 2: plazocobro de cartelería/hardware por RPC (linea_id), sin tocar el array por índice.
    const { error } = await client.rpc("actualizar_linea_facturacion", {
      p_linea_id: lineaId,
      p_campos: { plazocobro: nuevaFecha },
      p_usuario_id: usuarioIdInt,
    });
    if (error) throw error;
    return;
  }
  if (esCarteleria(tipo) && lineaId == null && claveNatural != null) {
    const { error } = await client.rpc("upsert_linea_facturacion", {
      p_negocio_id: String(claveNatural.negocioId),
      p_tipo: claveNatural.tipo,
      p_ano: String(claveNatural.ano),
      p_mes: String(claveNatural.mes),
      p_tramo: claveNatural.tramo ?? 1,
      p_campos: { plazocobro: nuevaFecha },
      p_usuario_id: usuarioIdInt,
    });
    if (error) throw error;
  }
};
